// How a stored automation run and its per-action results are read on screen.
//
// Runs written under docs/auth-and-admin/hq-customers-contract.md section 4 carry
// `mode` ("live" | "dry_run"), a run `status` (succeeded | partial | skipped | failed) and,
// per action, `{type, outcome, detail_code, detail_params}`. Rows stored before that
// contract carry `demo_mode` and a free-text `detail` instead; they are never rewritten, so
// the reader maps them here, in one place, and every screen that shows a run uses it.

import { configIssueText } from "./automationCodes";

const OUTCOMES = ["applied", "simulated", "skipped", "failed"];
// A dry run may also report the outcome under the "would" spelling the handlers use.
const DRY_ALIASES = { would_apply: "applied", would_skip: "skipped" };
const RUN_STATUSES = ["succeeded", "partial", "skipped", "failed", "running"];
// The display status of a legacy run whose actions were simulated (see runStatus).
export const LEGACY_SIMULATED = "simulated_legacy";
// Statuses the legacy runner stored for a whole run or one action.
const LEGACY_STATUS = { error: "failed" };

export const isDryRun = (run) => run?.mode === "dry_run";

/** Legacy rows have no `mode`/`outcome`; `demo_mode` was their only honesty signal. */
export const isLegacyAction = (result) => !result?.outcome;

/** The outcome of one action result: one of applied | simulated | skipped | failed. */
export function actionOutcome(result) {
  const outcome = result?.outcome;
  if (OUTCOMES.includes(outcome)) return outcome;
  if (DRY_ALIASES[outcome]) return DRY_ALIASES[outcome];
  // Legacy: demo_mode meant "nothing real happened", whatever the status said.
  if (result?.demo_mode) return "simulated";
  const status = LEGACY_STATUS[result?.status] || result?.status;
  if (status === "skipped" || status === "failed") return status;
  return "applied";
}

/** A run stored before runs carried a `mode`, whose `demo_mode` says nothing real happened. */
export const isLegacySimulated = (run) => !run?.mode && run?.demo_mode === true;

/**
 * The status of a whole run, normalised to a dictionary key (`hauto.status.<key>`); unknown
 * values read as "succeeded". A legacy simulated run that did not fail or is not still running
 * is `simulated_legacy`, never "succeeded": the old runner stored `succeeded` for a run whose
 * actions only pretended (`demo_mode`), and the screen must not repeat that claim.
 */
export function runStatus(run) {
  const raw = LEGACY_STATUS[run?.status] || run?.status;
  const status = RUN_STATUSES.includes(raw) ? raw : "succeeded";
  if (isLegacySimulated(run) && (status === "succeeded" || status === "partial" || status === "skipped")) return LEGACY_SIMULATED;
  return status;
}

// Params that hold a customer stage are shown by the stage's own label, not its stored key.
const STAGE_PARAMS = ["stage", "previous_stage"];

/**
 * The sentence for one action's detail. New rows: one dictionary key per `detail_code`,
 * filled from `detail_params`; a code nobody has written yet shows nothing rather than a raw
 * key. When the action created the customer row (or, in a dry run, would), that is added as a
 * second sentence. Legacy rows: the stored text as it was written.
 */
export function actionDetail(result, t, { dry = false } = {}) {
  if (!result?.detail_code) return typeof result?.detail === "string" ? result.detail : "";
  // An action the runner skipped without running it names what is wrong (contract 4.4): the
  // same sentence the rule list's "needs setup" marker uses. A row stored before the field
  // was reported keeps the general `hauto.detail.invalid_config`.
  const field = result.detail_params?.field;
  if (result.detail_code === "invalid_config" && typeof field === "string" && field) return configIssueText(t, field);
  const params = { ...(result.detail_params || {}) };
  for (const key of STAGE_PARAMS) {
    if (params[key]) params[key] = t(`hc.stage.${params[key]}`, undefined, params[key]);
  }
  const sentence = t(`hauto.detail.${result.detail_code}`, params, "");
  if (!params.customer_created) return sentence;
  return [sentence, t(dry ? "hauto.detail.customer_would_be_created" : "hauto.detail.customer_created")].filter(Boolean).join(" ");
}
